import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Heart } from "lucide-react";
import { SECTIONS, COLOR_HEX } from "@/data/navConfig";

const Navbar = () => {
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(null);
  const location = useLocation();

  return (
    <header
      data-testid="main-navbar"
      className="sticky top-0 z-50 border-b border-[#E5E5E5] bg-white/80 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group">
            <img src="/logo.png" alt="Juventudes Latentes" className="w-9 h-9 md:w-10 md:h-10 rounded-md" />
            <span className="hidden sm:flex items-baseline gap-1">
              <span className="font-heading font-bold text-xl md:text-2xl tracking-tight text-brand-ink">
                Juventudes
              </span>
              <span className="font-heading font-bold text-xl md:text-2xl tracking-tight text-gradient">
                Latentes
              </span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {SECTIONS.filter((s) => !s.hidden).map((s) =>
              s.subpages.length === 0 ? (
                <Link
                  key={s.key}
                  to={s.path}
                  data-testid={`nav-${s.key}-link`}
                  className="nav-underline text-sm font-semibold text-brand-ink py-2"
                >
                  {s.title}
                </Link>
              ) : (
                <div
                  key={s.key}
                  className="relative"
                  onMouseEnter={() => setOpen(s.key)}
                  onMouseLeave={() => setOpen(null)}
                >
                  <Link
                    to={s.path}
                    data-testid={`nav-${s.key}-link`}
                    className="nav-underline flex items-center gap-1 text-sm font-semibold text-brand-ink py-2"
                  >
                    {s.title}
                    <ChevronDown size={14} className="opacity-60" />
                  </Link>
                  {open === s.key && (
                    <div className="absolute left-0 top-full pt-2 w-64">
                      <div className="bg-white border border-[#E5E5E5] shadow-xl rounded-sm overflow-hidden">
                        <div
                          className="h-1 w-full"
                          style={{ background: COLOR_HEX[s.color] }}
                        />
                        {s.subpages.map((sub) => (
                          <Link
                            key={sub.slug}
                            to={`${s.path}/${sub.slug}`}
                            data-testid={`nav-${s.key}-${sub.slug}`}
                            className="block px-4 py-3 text-sm text-brand-ink hover:bg-[#FAF9F6] hover:pl-5 transition-all border-b border-[#F0F0F0] last:border-0"
                          >
                            {sub.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/donaciones/dona-ahora"
              data-testid="navbar-donate-btn"
              className="inline-flex items-center gap-2 bg-brand-ink text-white text-sm font-semibold px-5 py-2.5 rounded-sm hover:bg-brand-orange transition-colors duration-200"
            >
              <Heart size={15} /> Donar
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-brand-ink"
            onClick={() => setMobile(!mobile)}
          >
            {mobile ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobile && (
        <div className="lg:hidden border-t border-[#E5E5E5] bg-white max-h-[80vh] overflow-y-auto">
          {SECTIONS.filter((s) => !s.hidden).map((s) =>
            s.subpages.length === 0 ? (
              <Link
                key={s.key}
                to={s.path}
                data-testid={`mobile-nav-${s.key}`}
                onClick={() => setMobile(false)}
                className="block px-6 py-4 font-semibold text-brand-ink border-b border-[#F0F0F0]"
              >
                {s.title}
              </Link>
            ) : (
              <div key={s.key} className="border-b border-[#F0F0F0]">
                <button
                  data-testid={`mobile-nav-${s.key}`}
                  className="w-full flex items-center justify-between px-6 py-4 font-semibold text-brand-ink"
                  onClick={() => setMobileOpen(mobileOpen === s.key ? null : s.key)}
                >
                  {s.title}
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${mobileOpen === s.key ? "rotate-180" : ""}`}
                  />
                </button>
                {mobileOpen === s.key && (
                  <div className="bg-[#FAF9F6] pb-2">
                    {s.subpages.map((sub) => (
                      <Link
                        key={sub.slug}
                        to={`${s.path}/${sub.slug}`}
                        data-testid={`mobile-nav-${s.key}-${sub.slug}`}
                        onClick={() => setMobile(false)}
                        className="block px-8 py-2.5 text-sm text-brand-ink/80"
                      >
                        {sub.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )
          )}
          <div className="p-6">
            <Link
              to="/donaciones/dona-ahora"
              onClick={() => setMobile(false)}
              data-testid="mobile-donate-btn"
              className="block text-center bg-brand-ink text-white font-semibold px-5 py-3 rounded-sm"
            >
              Donar
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
